import { getLogs } from "api/graphQL_requestlogs";
import { useEffect, useState, useContext, useRef } from "react";
import toast from "react-hot-toast";
import { ClientContext } from "ClientProvider";
import { useLocation } from "react-router-dom";
import LogsTable from "./components/LogsList";
import { getAllClients } from "api/clients";

export interface LogsRowObj {
  clientId: string;
  clientName: string;
  customerId: string;
  customerName: string;
  endpoint: string;
  requestData: any;
  responseData: any;
  createdAt: any;
}

const ApiRequestLogs = () => {
  const [page, setPage] = useState<number>(1);
  const [logs, setLogs] = useState<LogsRowObj[]>([]);
  const [totalpage, setTotalPage] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTexts, setSearchTexts] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { singleRoleData, setSingleRoleData } = useContext(ClientContext);
  const location = useLocation();
  const [clients, setClients] = useState();
  const [localFilters, setLocalFilters] = useState<any>({});
  const [clearClicked, setClearClick] = useState(0);
  const [filter, setFilter] = useState({ dates: null, client: "" });
  const [loadingState, setLoadingState] = useState({ clients: true });

  useEffect(() => {
    fetchClients();
    setFilter({ dates: null, client: "" });
  }, []);

  const fetchClients = () => {
    setIsLoading(true);
    getAllClients(0)
      .then((data) => setClients(data))
      .catch((err) => toast.error(err?.response?.data?.message ?? "Something went wrong while fetching clients"))
      .finally(() => { setIsLoading(false); setLoadingState((prev) => ({ ...prev, clients: false })); });
  };

  const fetchLogs = async () => {
    let finalSearchText = "";
    if (location.state?.search) {
      finalSearchText = location.state.search;
      window.history.replaceState({}, document.title);
    } else {
      finalSearchText = searchTexts;
    }
    if (pageSize === null || pageSize === undefined) return;
    setIsLoading(true);
    setLogs([]);
    try {
      const data: any = await getLogs({ page, per_page: pageSize, searchTexts: finalSearchText, client: filter?.client, start_date: filter?.dates?.[0], end_date: filter?.dates?.[1] });
      setLogs(data?.[0] ?? []);
      setTotalPage(data?.[1]?.total_pages ?? 1);
      setTotalItems(data?.[1]?.total_items ?? 0);
      setCurrentPage(data?.[1]?.current_page ?? 1);
    } catch {
      setLogs([]); setTotalPage(1); setTotalItems(0); setCurrentPage(1);
    } finally {
      setIsLoading(false);
    }
  };

  const serchboxValueChange = async (searchTerm: string) => {
    const search_txt = searchTerm || "";
    if (search_txt !== searchTexts && searchTexts !== "") {
      await setSearchTexts(search_txt);
      setPage(1); setCurrentPage(1);
    } else {
      await setSearchTexts(search_txt);
    }
  };

  useEffect(() => { fetchLogs(); }, [page, pageSize, searchTexts, clearClicked]);
  useEffect(() => { setPage(1); setPageSize(50); }, [searchTexts]);

  const onClearClick = () => {
    setPage(1); setPageSize(50);
    setClearClick((prev) => prev + 1);
  };

  const isAllDataReady = Object.values(loadingState).every((v) => v === false);

  return (
    <div className="arl-root">
      {/* Page header */}
      <div className="arl-page-header">
        <div className="arl-title-block">
          <div className="arl-page-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
          </div>
          <div>
            <h1 className="arl-heading">API Request Logs</h1>
            <p className="arl-sub">Monitor incoming API requests and responses</p>
          </div>
        </div>
      </div>

      <LogsTable
        clients={clients}
        tableData={logs}
        fetchLogs={fetchLogs}
        isLoading={isLoading}
        roleData={singleRoleData}
        onValueChange={serchboxValueChange}
        page={page}
        setPage={setPage}
        totalpage={totalpage}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        setLocalFilters={setLocalFilters}
        filterData={fetchLogs}
        localFilters={localFilters}
        filter={filter}
        setFilter={setFilter}
        setClearClick={onClearClick}
        isAllDataReady={isAllDataReady}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .arl-root { font-family:'Plus Jakarta Sans',sans-serif; display:flex; flex-direction:column; gap:20px; }
        .arl-page-header { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:16px; }
        .arl-title-block { display:flex; align-items:center; gap:14px; }
        .arl-page-icon {
          width:44px; height:44px; border-radius:12px;
          background:linear-gradient(135deg,#f0f9ff,#e0f2fe);
          border:1.5px solid #bae6fd;
          display:flex; align-items:center; justify-content:center;
          color:#0284c7; flex-shrink:0;
        }
        .arl-heading { font-size:20px; font-weight:800; color:#0f172a; letter-spacing:-0.4px; margin-bottom:2px; line-height:1.2; }
        .arl-sub { font-size:13px; color:#94a3b8; font-weight:500; }
      `}</style>
    </div>
  );
};

export default ApiRequestLogs;